/**
 * InviteDialog — bottom-sheet with WhatsApp / SMS / Copy-link share buttons.
 *
 * Automatically appends the current user's referral code to the share URL so
 * when the invited friend signs up through it, the backend's
 * `referral_domestic` / `referral_international` point award fires — the
 * inviter earns points without any manual tracking.
 *
 * On iOS Safari we deep-link to `whatsapp://` and `sms:` so the user lands
 * directly in the chat composer with text pre-filled. Web Share API is used
 * when available; clipboard copy is the last-resort fallback.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, MessageCircle, Phone, Link as LinkIcon, Share2, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = 'https://www.baremc.com';
const REGISTER_PATH = '/register';

export const InviteDialog = ({
  open,
  onClose,
  message = 'Kom med på BareMC — socialt MC-netværk for os der brænder for to hjul.',
  title = 'Inviter venner',
  // If the caller wants to share a specific page (e.g. a club), it can override.
  urlOverride = null,
}) => {
  const { t } = useLanguage();
  const [referralCode, setReferralCode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    setLoading(true);
    axios.get(`${API_URL}/points/me`)
      .then(res => { if (!cancel) setReferralCode(res.data?.referral_code || null); })
      .catch(() => { /* non-fatal — dialog still works, just no points bonus */ })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [open]);

  if (!open) return null;

  // Build share URL: base + /register?ref=CODE so the signup flow auto-attributes
  // the invite → inviter gets 35 pts (domestic) / 50 pts (international) on signup.
  const buildUrl = () => {
    const base = urlOverride || `${BASE_URL}${REGISTER_PATH}`;
    if (!referralCode) return base;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}ref=${encodeURIComponent(referralCode)}`;
  };

  const url = buildUrl();
  const fullText = `${message} ${url}`;

  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(fullText)}`, '_blank', 'noopener,noreferrer');
  };

  const openSMS = () => {
    window.location.href = `sms:?&body=${encodeURIComponent(fullText)}`;
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'BareMC', text: message, url }); } catch {}
    } else {
      copyLink();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      toast.success(t('common.linkCopied'));
    } catch {
      try { window.prompt(t('common.linkCopied'), fullText); } catch { /* noop */ }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[900] bg-black/70 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="invite-dialog"
    >
      <div
        className="w-full sm:max-w-md bg-zinc-900 rounded-t-2xl sm:rounded-2xl border border-white/10"
        style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h2 className="text-white font-bold text-sm">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="invite-close" aria-label={t('gen.fcadab5fc5')}>
            <X className="w-4 h-4 text-white" />
          </button>
        </header>

        {/* Points hint — motivates the user to actually share */}
        {referralCode && (
          <div className="mx-4 mt-3 p-3 rounded-xl bg-[#00FF88]/10 border border-[#00FF88]/30 flex items-start gap-2" data-testid="invite-bonus-info">
            <Gift className="w-4 h-4 text-[#00FF88] flex-shrink-0 mt-0.5" />
            <p className="text-[#00FF88] text-xs font-semibold leading-relaxed">
              Få <strong>35 point</strong> {t('gen.d7ffe35859')} <strong>25 ekstra</strong> efter deres første klub-check-in.
            </p>
          </div>
        )}

        <div className="p-4 space-y-2">
          <p className="text-white/60 text-xs mb-1">
            {loading ? 'Henter din invite-kode…' : (referralCode ? `Din kode: ${referralCode}` : 'Del BareMC med dine venner')}
          </p>

          <button
            onClick={openWhatsApp}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#25D366]/15 border border-[#25D366]/40 text-white font-semibold active:scale-[0.98] transition"
            data-testid="invite-whatsapp"
          >
            <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-white" />
            </div>
            <span>{t('invite.shareWhatsApp')}</span>
          </button>

          <button
            onClick={openSMS}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-blue-500/15 border border-blue-500/40 text-white font-semibold active:scale-[0.98] transition"
            data-testid="invite-sms"
          >
            <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <span>{t('gen.a76483d728')}</span>
          </button>

          <button
            onClick={nativeShare}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 text-white font-semibold active:scale-[0.98] transition"
            data-testid="invite-native"
          >
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
              <Share2 className="w-4 h-4 text-white" />
            </div>
            <span>{t('invite.moreOptions')}</span>
          </button>

          <button
            onClick={copyLink}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 text-white font-semibold active:scale-[0.98] transition"
            data-testid="invite-copy"
          >
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
              <LinkIcon className="w-4 h-4 text-white" />
            </div>
            <span>{t('invite.copyLink')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteDialog;
